import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  InputAdornment,
  Divider,
} from '@mui/material';
import { Search, Delete, Warning, Visibility } from '@mui/icons-material';
import axios from 'axios';
import { DATA_API_URL, CAM_API_URL,CLIENT_ID } from '../config';
import { useNavigate } from 'react-router-dom';

interface ImageData {
  id: number;
  s3_url: string;
  cam_preview_url: string;
}

interface TruckEntry {
  truck_entry_id: number;
  truck_number: string;
  height: number;
  length: number;
  breadth: number;
  light_condition: string;
  climate: string;
  truck_type: string;
  entry_type: string;
  bed_color: string;
  client_id: string;
  created_on: string;
  images: ImageData[];
  status?: string;
}

interface SearchParams {
  truckNumber: string;
  startDate: string;
  endDate: string;
}

const ImageList: React.FC = () => {
  const [entries, setEntries] = useState<TruckEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<TruckEntry[]>([]);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    truckNumber: '',
    startDate: '',
    endDate: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedEntry, setSelectedEntry] = useState<TruckEntry | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<number | null>(null);
  const navigate = useNavigate();
  const clientId = process.env.REACT_APP_CLIENT_ID;

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchParams.truckNumber) params.append('truck_number', searchParams.truckNumber);
      if (searchParams.startDate) params.append('start_date', searchParams.startDate);
      if (searchParams.endDate) params.append('end_date', searchParams.endDate);

      const response = await axios.get(`${DATA_API_URL}/data-server/v1/truck-entry?${params}`, {
        headers: {
          'X-Client-Id': CLIENT_ID
        }
      });
      setEntries(response.data);
    } catch (err) {
      setError('Error fetching entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    filterEntries();
  }, [entries, searchParams]);

  const filterEntries = () => {
    let filtered = [...entries];

    // Filter by truck number
    if (searchParams.truckNumber) {
      filtered = filtered.filter(entry =>
        entry.truck_number.toLowerCase().includes(searchParams.truckNumber.toLowerCase())
      );
    }

    // Filter by date range
    if (searchParams.startDate) {
      const startDate = new Date(searchParams.startDate);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.created_on);
        entryDate.setHours(0, 0, 0, 0);
        return entryDate >= startDate;
      });
    }

    if (searchParams.endDate) {
      const endDate = new Date(searchParams.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.created_on);
        return entryDate <= endDate;
      });
    }

    setFilteredEntries(filtered);
    setPage(0); // Reset to first page when filtering
  };

  const handleSearchChange = (field: keyof SearchParams, value: string) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleRow = (truckEntryId: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(truckEntryId)) {
      newExpandedRows.delete(truckEntryId);
    } else {
      newExpandedRows.add(truckEntryId);
    }
    setExpandedRows(newExpandedRows);
  };


  const handleDelete = async (entryId: number) => {
    try {
      await axios.delete(`${DATA_API_URL}/data-server/v1/truck-entry/${entryId}`, {
        headers: {
          'X-Client-Id': clientId || ''
        }
      });
      setEntries(entries.filter(entry => entry.truck_entry_id !== entryId));
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry');
    }
  };

  const handleMarkAsWrong = async (entryId: number) => {
    try {
      await axios.patch(`${DATA_API_URL}/data-server/v1/truck-entry/${entryId}`, 
        { status: 'wrong' },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Client-Id': clientId || ''
          }
        }
      );
      setEntries(entries.map(entry => 
        entry.truck_entry_id === entryId 
          ? { ...entry, status: 'wrong' }
          : entry
      ));
    } catch (error) {
      console.error('Error marking entry as wrong:', error);
      alert('Failed to mark entry as wrong');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewImages = (entry: TruckEntry) => {
    setSelectedEntry(entry);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEntry(null);
  };

  const handleOpenDeleteDialog = (id: number) => {
    setEntryToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setEntryToDelete(null);
  };

  const renderTruckDetails = (entry: TruckEntry) => (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Truck Number</Typography>
          <Typography variant="body1">{entry.truck_number}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Status</Typography>
          <Typography variant="body1">{entry.status || 'Not marked'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Dimensions</Typography>
          <Typography variant="body1">
            H: {entry.height}m, L: {entry.length}m, B: {entry.breadth}m
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Truck Type</Typography>
          <Typography variant="body1">{entry.truck_type}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Entry Type</Typography>
          <Typography variant="body1">{entry.entry_type}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Bed Color</Typography>
          <Typography variant="body1">{entry.bed_color}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Light Condition</Typography>
          <Typography variant="body1">{entry.light_condition}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Climate</Typography>
          <Typography variant="body1">{entry.climate}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">Created On</Typography>
          <Typography variant="body1">{formatDate(entry.created_on)}</Typography>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Search by Truck Number"
            variant="outlined"
            value={searchParams.truckNumber}
            onChange={(e) => handleSearchChange('truckNumber', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="From Date"
            type="date"
            value={searchParams.startDate}
            onChange={(e) => handleSearchChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
          <TextField
            label="To Date"
            type="date"
            value={searchParams.endDate}
            onChange={(e) => handleSearchChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Truck Number</TableCell>
                <TableCell>Dimensions</TableCell>
                <TableCell>Truck Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created On</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((entry) => (
                  <TableRow key={entry.truck_entry_id}>
                    <TableCell>{entry.truck_number}</TableCell>
                    <TableCell>H: {entry.height}m, L: {entry.length}m, B: {entry.breadth}m</TableCell>
                    <TableCell>{entry.truck_type}</TableCell>
                    <TableCell>{entry.status || 'Not marked'}</TableCell>
                    <TableCell>{formatDate(entry.created_on)}</TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleViewImages(entry)}
                        title="View Details & Images"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(entry.truck_entry_id)}
                        title="Delete"
                      >
                        <Delete />
                      </IconButton>
                      <IconButton
                        color="warning"
                        onClick={() => handleMarkAsWrong(entry.truck_entry_id)}
                        title="Mark as Wrong"
                      >
                        <Warning />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredEntries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Image Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="image-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            overflow: 'auto',
          }}
        >
          <Typography variant="h5" id="image-modal-title" gutterBottom>
            Truck Details & Images
          </Typography>
          {selectedEntry && renderTruckDetails(selectedEntry)}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>
            Images
          </Typography>
          <Grid container spacing={2}>
            {selectedEntry?.images.map((image) => (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <img
                  src={image.s3_url}
                  onError={(e) => {
                    e.currentTarget.onerror = null; 
                    e.currentTarget.src = image.cam_preview_url.replace(/^https?:\/\/[^/]+/, CAM_API_URL);
                  }}
                  alt={`Truck ${selectedEntry.truck_number}`}
                  style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseModal} variant="contained">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this entry?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={() => entryToDelete && handleDelete(entryToDelete)}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageList;
